// Import functions from Redux Toolkit
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// Import API service functions to fetch and search profiles
import { getProfiles, searchProfiles, addProfile, getProfilesByDescription, editProfile, deleteProfile, updateProfiles, getProfilesOfCurrentUser } from '../services/apiService';

// Create an asynchronous action to fetch profiles
export const fetchProfiles = createAsyncThunk(
  'profiles/fetchProfiles',
  async (_, { getState, rejectWithValue }) => {
    // Access the current state of profiles and filters from Redux
    const { profiles: { currentPage }, filters } = getState();
    const {
      searchTerm, localisation, competences, certifications,
      formations, modeTravail, tempsTravail, priceRange,
      competencesLogicOperator, certificationsLogicOperator,
      formationsLogicOperator, anneesExperienceRange, inter_exp
    } = filters;

    // Transform text fields into arrays (split by commas)
    const competencesArray = competences ? competences.split(',').map(c => c.trim()).filter(Boolean) : [];
    const certificationsArray = certifications ? certifications.split(',').map(c => c.trim()).filter(Boolean) : [];
    const formationsArray = formations ? formations.split(',').map(f => f.trim()).filter(Boolean) : [];

    // Check if any filters are applied
    const hasFilters = searchTerm || localisation ||
      competencesArray.length > 0 || certificationsArray.length > 0 || formationsArray.length > 0 ||
      modeTravail || tempsTravail || priceRange[0] > 0 || priceRange[1] < 2000 || anneesExperienceRange[0] > 0 || anneesExperienceRange[1] < 50 || inter_exp;

    try {
      if (hasFilters) {
        // Create an object containing the filters for the search
        const searchFilters = {
          poste: searchTerm,
          localisation,
          competences: competencesArray,
          competences_operator: competencesLogicOperator,
          certifications: certificationsArray,
          certifications_operator: certificationsLogicOperator,
          formations: formationsArray,
          formations_operator: formationsLogicOperator,
          mode_travail: modeTravail,
          temps_travail: tempsTravail,
          tarif_min: priceRange[0],
          tarif_max: priceRange[1],
          annees_experience_min: undefined, // Remove unused range filters
          annees_experience_max: undefined, // Remove unused range filters
          inter_exp, // Add inter_exp filter
          page: currentPage,
          limit: 10, // Maximum number of results per page
        };
        // Call the searchProfiles function with the filters
        const data = await searchProfiles(searchFilters);
        return data; // Return the fetched data
      } else {
        // If no filters are applied, fetch profiles by page
        const data = await getProfiles({ page: currentPage, limit: 10 });
        return data; // Return the fetched data
      }
    } catch (error) {
      // Handle errors by returning a custom error message
      return rejectWithValue("Une erreur s est produite lors de la récupération des profils.");
    }
  }
);

// Create an asynchronous action to add a profile
export const createProfile = createAsyncThunk(
  'profiles/createProfile',
  async ({ profileData, cvFile, imageFile }, { rejectWithValue }) => {
    try {
      const data = await addProfile(profileData, cvFile, imageFile);
      const updateResponse = await updateProfiles(data.profile.id);
      return { ...data, updateResponse };
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de l'ajout du profil.");
    }
  }
);

export const fetchProfilesByDescription = createAsyncThunk(
  'profiles/fetchProfilesByDescription',
  async ({ description, numberOfProfiles, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await getProfilesByDescription({ description, numberOfProfiles });
      if (response.message) {
        return { message: response.message, data: [], total: 0 };
      } else {
        const profilesArray = response.data.map(profile => ({
          ...profile,
          scoreCompatibilite: profile["Score de compatibilité (%)"]
        }));
        const startIndex = (page - 1) * limit;
        const paginatedProfiles = profilesArray.slice(startIndex, startIndex + limit);
        return { data: paginatedProfiles, total: profilesArray.length, allData: profilesArray };
      }
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la récupération des profils avec chatbot");
    }
  }
);

// Create an asynchronous action to edit a profile
export const updateProfile = createAsyncThunk(
  'profiles/updateProfile',
  async ({ id, profileData }, { rejectWithValue }) => {
    try {
      const data = await editProfile(id, profileData);
      return data; // Return the updated profile data
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la mise à jour du profil.");
    }
  }
);

// Create an asynchronous action to delete a profile
export const removeProfile = createAsyncThunk(
  'profiles/removeProfile',
  async (id, { rejectWithValue }) => {
    try {
      await deleteProfile(id);
      return id; // Return the deleted profile ID
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la suppression du profil.");
    }
  }
);

// Create an asynchronous action to fetch profiles of the current user
export const fetchProfilesOfCurrentUser = createAsyncThunk(
  'profiles/fetchProfilesOfCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getProfilesOfCurrentUser();
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la récupération des profils de l'utilisateur.");
    }
  }
);

// Create a Redux slice to manage profiles
const profilesSlice = createSlice({
  name: 'profiles', // Name of the slice
  initialState: {
    profiles: [], // List of profiles
    totalProfiles: 0, // Total number of profiles
    currentPage: 1, // Current page for pagination
    loading: false, // Loading indicator
    error: null, // Error message
    isChatSearch: false, // Flag to indicate if profiles are fetched by chat
    chatProfiles: [], // Store profiles fetched by chat
    currentUserProfiles: [], // Add state for current user's profiles
    searchPerformed: false, // Add state to track if a search has been performed
  },
  reducers: {
    // Update the current page
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
      clearError: (state) => {
    state.error = null;
  },
    // Reset pagination to the first page
    resetPagination(state) {
      state.currentPage = 1;
    },
    setChatSearch(state, action) {
      state.isChatSearch = action.payload;
    },
    setProfiles(state, action) {
      state.profiles = action.payload;
    },
    setSearchPerformed(state, action) {
      state.searchPerformed = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle the states of the asynchronous action
    builder
      .addCase(fetchProfiles.pending, (state) => {
        state.loading = true; // Activate the loading indicator
        state.error = null; // Reset errors
      })
      .addCase(fetchProfiles.fulfilled, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        state.isChatSearch = false; // Reset chat search flag
        // Update the profiles and the total number of profiles
        state.profiles = Array.isArray(action.payload.data) ? action.payload.data : action.payload.data || [];
        state.totalProfiles = action.payload.total || 0;
        state.searchPerformed = true; // Set search performed to true
      })
      .addCase(fetchProfiles.rejected, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        // Save the error message
        state.error = action.payload || "Une erreur s'est produite lors de la récupération des profils.";
      })
      .addCase(createProfile.pending, (state) => {
        state.loading = true; // Activate the loading indicator
        state.error = null; // Reset errors
      })
      .addCase(createProfile.fulfilled, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        state.profiles.push(action.payload.profile); // Ensure the new profile is added to the profiles array
      })
      .addCase(createProfile.rejected, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        state.error = action.payload || "Une erreur s'est produite lors de l'ajout du profil";
      })
      .addCase(fetchProfilesByDescription.pending, (state) => {
        state.loading = true; // Activate the loading indicator
        state.error = null; // Reset errors
        state.message = null; // Reset message
      })
      .addCase(fetchProfilesByDescription.fulfilled, (state, action) => {
        state.loading = false;
       // state.isChatSearch = true; // Set chat search flag
        if (action.payload.message) {
          state.message = action.payload.message;
          state.profiles = [];
          state.totalProfiles = 0;
        } else {
          state.chatProfiles = action.payload.allData; // Store all profiles fetched by chat
         // state.profiles = action.payload.data; // Display first page of profiles
          //state.totalProfiles = action.payload.allData.length;
        }
      })
      .addCase(fetchProfilesByDescription.rejected, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        // Save the error message
        state.error = action.payload || "Une erreur s'est produite lors de la récupération des profils avec chatbot";
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true; // Activate the loading indicator
        state.error = null; // Reset errors
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
              state.error   = null;     // <-- on clear l'erreur ici aussi

      // Mise à jour dans la liste générale
      const idx = state.profiles.findIndex(p => p.id === action.payload.id);
      if (idx > -1) state.profiles[idx] = action.payload;

      // Et dans la liste "currentUserProfiles" si présente
      const idx2 = state.currentUserProfiles.findIndex(p => p.id === action.payload.id);
      if (idx2 > -1) state.currentUserProfiles[idx2] = action.payload;
    })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        state.error = action.payload || "Une erreur s'est produite lors de la mise à jour du profil.";
      })
      .addCase(removeProfile.pending, (state) => {
        state.loading = true; // Activate the loading indicator
        state.error = null; // Reset errors
      })
      .addCase(removeProfile.fulfilled, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        state.error = null; 
        state.profiles = state.profiles.filter(profile => profile.id !== action.payload);
      })
      .addCase(removeProfile.rejected, (state, action) => {
        state.loading = false; // Deactivate the loading indicator
        // Save the error message
        state.error = action.payload || "Une erreur s'est produite lors de la suppression du profil.";
      })
      .addCase(fetchProfilesOfCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfilesOfCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUserProfiles = action.payload.data;
      })
      .addCase(fetchProfilesOfCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Une erreur s'est produite lors de la récupération des profils de l'utilisateur.";
      });
  },
});

// Export actions to use them in components
export const { setCurrentPage, clearError,resetPagination, setChatSearch, setProfiles, setSearchPerformed } = profilesSlice.actions;

// Export the reducer to include it in the Redux store
export default profilesSlice.reducer;


